// @spec SUP-STR-017
'use client';

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  QuestionnaireTemplateForm,
  useQuestionnaireTemplate,
  useUpdateTemplate,
} from '@/features/suppliers/admin/questionnaire-templates';
import type {
  CreateTemplateInput,
  UpdateTemplateInput,
} from '@/features/suppliers/admin/questionnaire-templates';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute(
  '/_authenticated/suppliers/admin/questionnaire-templates/$templateId'
)({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.editQuestionnaireTemplate'),
  }),
  component: EditQuestionnaireTemplatePage,
});

/**
 * Edit questionnaire template page.
 *
 * Loads an existing questionnaire template and displays it in the
 * QuestionnaireTemplateForm for editing. Users can:
 * - View template details and structure
 * - Edit template metadata, sections, and questions
 * - Configure scoring rules
 * - Save changes to the template
 *
 * Note: Templates that have been sent to suppliers become read-only.
 * Editing a sent template will create a new version.
 */
function EditQuestionnaireTemplatePage() {
  const { t } = useTranslation(['suppliers', 'common']);
  const navigate = useNavigate();
  const { templateId } = Route.useParams();
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);

  // Fetch the questionnaire template
  const {
    data: template,
    isLoading: templateLoading,
    error,
  } = useQuestionnaireTemplate({
    templateId,
    enabled: Boolean(activeOrg?.id),
  });

  const updateTemplate = useUpdateTemplate({
    onSuccess: () => {
      toast.success(t('suppliers:toast.templateUpdatedSuccessfully'));
      void navigate({ to: '/suppliers/admin/questionnaire-templates' });
    },
    onError: (error) => {
      toast.error(error.message || i18n.t('suppliers:toast.failedToUpdateTemplate'));
    },
  });

  const handleSubmit = (data: CreateTemplateInput | UpdateTemplateInput): Promise<void> => {
    updateTemplate.mutate({ templateId, data: data as UpdateTemplateInput });
    return Promise.resolve();
  };

  const handleCancel = () => {
    void navigate({ to: '/suppliers/admin/questionnaire-templates' });
  };

  // Show loading while checking organizations or fetching template
  if (orgsLoading || templateLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 rounded bg-muted" />
          <div className="h-64 w-96 rounded bg-muted" />
        </div>
      </div>
    );
  }

  // No active organization selected
  if (!activeOrg) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="rounded-lg border border-dashed p-12 text-center">
          <h2 className="text-lg font-semibold text-foreground">{t('common:routeErrors.noOrgSelected')}</h2>
          <p className="mt-2 text-muted-foreground">
            {t('common:routeErrors.noOrgSelectedDesc')}
          </p>
          <Button variant="outline" asChild className="mt-4">
            <Link to="/settings/organizations">{t('common:routeErrors.goToOrganizations')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Error loading template
  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="max-w-md rounded-lg border border-destructive/50 bg-destructive/10 p-8 text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-destructive" />
          <h2 className="mt-4 text-lg font-semibold text-foreground">Failed to Load Template</h2>
          <p className="mt-2 text-muted-foreground">
            {error instanceof Error
              ? error.message
              : 'An error occurred while loading the questionnaire template.'}
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button variant="outline" asChild>
              <Link to="/suppliers/admin/questionnaire-templates">{t('common:routeErrors.backToTemplates')}</Link>
            </Button>
            <Button
              onClick={() => {
                window.location.reload();
              }}
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Template not found
  if (!template) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="rounded-lg border border-dashed p-12 text-center">
          <h2 className="text-lg font-semibold text-foreground">Template Not Found</h2>
          <p className="mt-2 text-muted-foreground">
            The questionnaire template you&apos;re looking for doesn&apos;t exist or has been
            deleted.
          </p>
          <Button variant="outline" asChild className="mt-4">
            <Link to="/suppliers/admin/questionnaire-templates">Back to Templates</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl">
        <QuestionnaireTemplateForm
          mode="edit"
          template={template}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isPending={updateTemplate.isPending}
        />
      </div>
    </div>
  );
}
